import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const PROJECT_TYPES = [
    "Rénovation complète",
    "Cuisine",
    "Salle de bain",
    "Aménagement sur-mesure",
    "Bureaux / Local pro",
    "Autre",
];

const BUDGETS = ["< 30 000 €", "30 000 - 80 000 €", "80 000 - 150 000 €", "150 000 - 300 000 €", "> 300 000 €"];
const TIMELINES = ["Immédiat", "Dans 3 mois", "Dans 6 mois", "Plus tard"];

export default function Devis() {
    const [form, setForm] = useState({
        first_name: "", last_name: "", email: "", phone: "",
        project_type: "", surface: "", budget: "", location: "",
        timeline: "", message: "", consent: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const update = (k, v) => setForm({ ...form, [k]: v });

    const validate = () => {
        const errs = {};
        if (!form.first_name.trim()) errs.first_name = "Champ requis";
        if (!form.last_name.trim()) errs.last_name = "Champ requis";
        if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Email invalide";
        if (form.phone.trim().length < 6) errs.phone = "Numéro invalide";
        if (!form.project_type) errs.project_type = "Sélectionnez un type";
        if (!form.location.trim()) errs.location = "Champ requis";
        if (form.message.trim().length < 10) errs.message = "Décrivez votre projet (min. 10 caractères)";
        if (!form.consent) errs.consent = "Acceptez la politique de confidentialité";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!validate()) return;
        setSubmitting(true);
        try {
            await api.post("/quotes", form);
            setDone(true);
            toast.success("Votre demande a bien été transmise.");
        } catch (err) {
            const msg = formatApiError(err);
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center" data-testid="devis-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-brand-sage/10 flex items-center justify-center">
                    <Check size={28} className="text-brand-sage" strokeWidth={1.5} />
                </div>
                <h1 className="mt-8 font-serif text-4xl md:text-5xl font-light text-brand-ink">
                    Merci pour votre <span className="italic">confiance</span>.
                </h1>
                <p className="mt-6 text-lg text-brand-sageDark font-light">
                    Nous avons bien reçu votre demande. Un membre de Japet Agencement vous recontactera sous 48h ouvrées
                    pour échanger sur votre projet.
                </p>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20" data-testid="devis-page">
            <Reveal>
                <span className="overline">Demande de devis</span>
                <h1 className="mt-6 font-serif text-6xl md:text-7xl font-light text-brand-ink leading-[1.02]">
                    Parlons de
                    <span className="italic"> votre projet</span>.
                </h1>
                <p className="mt-6 text-xl text-brand-sageDark font-light max-w-2xl">
                    Renseignez les informations ci-dessous. Plus votre brief est précis, plus nous serons à même de
                    vous proposer une réponse adaptée.
                </p>
            </Reveal>

            <form onSubmit={submit} className="mt-16 space-y-12" data-testid="devis-form" noValidate>
                {/* Identité */}
                <div>
                    <h2 className="overline mb-6">Vos coordonnées</h2>
                    <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                        <Field label="Prénom" required error={fieldErrors.first_name}>
                            <input
                                type="text"
                                className="field-input"
                                value={form.first_name}
                                onChange={(e) => update("first_name", e.target.value)}
                                data-testid="devis-first-name"
                                autoComplete="given-name"
                            />
                        </Field>
                        <Field label="Nom" required error={fieldErrors.last_name}>
                            <input
                                type="text"
                                className="field-input"
                                value={form.last_name}
                                onChange={(e) => update("last_name", e.target.value)}
                                data-testid="devis-last-name"
                                autoComplete="family-name"
                            />
                        </Field>
                        <Field label="Email" required error={fieldErrors.email}>
                            <input
                                type="email"
                                className="field-input"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                data-testid="devis-email"
                                autoComplete="email"
                            />
                        </Field>
                        <Field label="Téléphone" required error={fieldErrors.phone}>
                            <input
                                type="tel"
                                className="field-input"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                data-testid="devis-phone"
                                autoComplete="tel"
                            />
                        </Field>
                    </div>
                </div>

                {/* Projet */}
                <div>
                    <h2 className="overline mb-6">Votre projet</h2>
                    <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                        <Field label="Type de projet" required error={fieldErrors.project_type}>
                            <select
                                className="field-input bg-transparent"
                                value={form.project_type}
                                onChange={(e) => update("project_type", e.target.value)}
                                data-testid="devis-project-type"
                            >
                                <option value="">Sélectionner…</option>
                                {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Surface estimée">
                            <input
                                type="text"
                                placeholder="ex. 80 m²"
                                className="field-input"
                                value={form.surface}
                                onChange={(e) => update("surface", e.target.value)}
                                data-testid="devis-surface"
                            />
                        </Field>
                        <Field label="Budget envisagé">
                            <select
                                className="field-input bg-transparent"
                                value={form.budget}
                                onChange={(e) => update("budget", e.target.value)}
                                data-testid="devis-budget"
                            >
                                <option value="">Sélectionner…</option>
                                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                        </Field>
                        <Field label="Localisation" required error={fieldErrors.location}>
                            <input
                                type="text"
                                placeholder="ex. Paris XVIe"
                                className="field-input"
                                value={form.location}
                                onChange={(e) => update("location", e.target.value)}
                                data-testid="devis-location"
                            />
                        </Field>
                        <Field label="Démarrage souhaité" className="md:col-span-2">
                            <div className="flex flex-wrap gap-3">
                                {TIMELINES.map((t) => (
                                    <button
                                        type="button"
                                        key={t}
                                        onClick={() => update("timeline", t)}
                                        data-testid={`devis-timeline-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                        className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                            form.timeline === t
                                                ? "bg-brand-sage text-brand-bg border-brand-sage"
                                                : "border-brand-sage/40 text-brand-sageDark hover:border-brand-sage"
                                        }`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                        </Field>
                    </div>
                </div>

                {/* Message */}
                <div>
                    <Field label="Décrivez votre projet" required error={fieldErrors.message}>
                        <textarea
                            rows={6}
                            className="field-input field-textarea"
                            placeholder="Surface, contraintes, inspirations, délais…"
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            data-testid="devis-message"
                        />
                    </Field>
                </div>

                {/* Consent */}
                <div>
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            className="mt-1.5 w-4 h-4 accent-brand-sage"
                            checked={form.consent}
                            onChange={(e) => update("consent", e.target.checked)}
                            data-testid="devis-consent"
                        />
                        <span className="text-sm text-brand-sageDark font-light">
                            J'accepte que mes données soient utilisées pour le traitement de ma demande conformément
                            à la politique de confidentialité. Aucune donnée n'est transmise à des tiers.
                        </span>
                    </label>
                    {fieldErrors.consent && (
                        <p className="mt-2 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14} /> {fieldErrors.consent}</p>
                    )}
                </div>

                {error && (
                    <p className="text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14} /> {error}</p>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="devis-submit"
                    className="px-10 py-5 bg-brand-ink text-brand-bg text-sm uppercase tracking-[0.22em] hover:bg-brand-sage transition-colors disabled:opacity-50"
                >
                    {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>
            </form>
        </div>
    );
}

function Field({ label, required, error, children, className = "" }) {
    return (
        <div className={className}>
            <label className="block overline mb-3">
                {label} {required && <span className="text-brand-sage">*</span>}
            </label>
            {children}
            {error && (
                <p className="mt-2 text-xs text-red-700 flex items-center gap-1.5"><AlertCircle size={12} /> {error}</p>
            )}
        </div>
    );
}
